import { FC } from 'react';
import { Tag, Skeleton } from 'antd';
import { BulbOutlined, ConsoleSqlOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';

import { useFelaEnhanced } from 'hooks';

import * as felaRules from './AnswerCard.rules';
import { AssistantAnswer, AssistantMode } from '../../types';
import { EvidencePanel } from '../EvidencePanel';

interface AnswerCardProps {
    answer?: AssistantAnswer;
    loading: boolean;
    pendingQuestion?: string;
}

const AnswerCard: FC<AnswerCardProps> = ({ answer, loading, pendingQuestion }) => {
    const isSql = answer?.mode === AssistantMode.SQL;
    const { styles } = useFelaEnhanced(felaRules, { mode: answer?.mode ?? AssistantMode.RAG });
    const intl = useIntl();

    if (loading && !answer) {
        return (
            <div className={styles.container}>
                <div className={styles.questionRow}>
                    {intl.formatMessage({ id: 'assistant.answer.asking' })}
                </div>
                <div className={styles.question}>{pendingQuestion}</div>
                <div className={styles.skeletonWrapper}>
                    <Skeleton active title={false} paragraph={{ rows: 3 }} />
                </div>
            </div>
        );
    }

    if (!answer) {
        return null;
    }

    return (
        <div className={styles.container}>
            <div>
                <div className={styles.questionRow}>
                    {intl.formatMessage({ id: 'assistant.answer.you' })}
                </div>
                <div className={styles.question}>{answer.question}</div>
            </div>
            <div className={styles.tagRow}>
                <Tag className={styles.modeTag}>
                    {isSql ? <ConsoleSqlOutlined /> : <BulbOutlined />}
                    {intl.formatMessage({
                        id: isSql ? 'assistant.mode.tag.sql' : 'assistant.mode.tag.rag',
                    })}
                </Tag>
            </div>
            <div className={styles.answer}>{answer.answer}</div>
            {answer.meta && (
                <div className={styles.meta}>
                    <FormattedMessage
                        id={isSql ? 'assistant.answer.meta.sql' : 'assistant.answer.meta.rag'}
                        values={{
                            provider: answer.meta.provider,
                            model: answer.meta.model,
                            promptVersion: answer.meta.promptVersion,
                            retrievedCount: answer.meta.retrievedCount,
                        }}
                    />
                </div>
            )}
            <EvidencePanel answer={answer} />
        </div>
    );
};

export default AnswerCard;
